import React from "react";
import { useCurrentFrame, useVideoConfig, spring, interpolate } from "remotion";

interface MotionCardProps {
  children: React.ReactNode;
  delay?: number;
  width?: number | string;
  style?: React.CSSProperties;
  enableGlossySweep?: boolean;
  enableFloatingBob?: boolean;
}

export const MotionCard: React.FC<MotionCardProps> = ({
  children,
  delay = 0,
  width = "100%",
  style = {},
  enableGlossySweep = true,
  enableFloatingBob = true,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Entrance spring animation
  const cardSpring = spring({
    frame: frame - delay,
    fps,
    config: {
      damping: 14,
      stiffness: 140,
      mass: 0.9,
    },
  });

  // 3D perspective transforms
  const rotateX = interpolate(cardSpring, [0, 1], [16, 0]);
  const rotateY = interpolate(cardSpring, [0, 1], [-8, 0]);
  const translateY = interpolate(cardSpring, [0, 1], [100, 0]);
  const scale = interpolate(cardSpring, [0, 1], [0.88, 1.0]);
  const opacity = interpolate(cardSpring, [0, 0.4, 1], [0, 0.8, 1]);

  // Gentle harmonic idle floating
  const floatY = enableFloatingBob && frame > delay + 20
    ? Math.sin((frame - delay) / 24) * 6
    : 0;

  // Glossy sheen sweep across card
  const sweepProgress = interpolate(
    (frame - delay) % 180,
    [30, 70],
    [-150, 250],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );

  return (
    <div
      style={{
        width,
        perspective: 1200,
        opacity,
        transform: `translateY(${translateY + floatY}px) scale(${scale}) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
        transition: "transform 0.05s linear",
      }}
    >
      <div
        style={{
          position: "relative",
          borderRadius: 24,
          overflow: "hidden",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          boxShadow:
            "0 25px 60px -12px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.12)",
          ...style,
        }}
      >
        {children}

        {/* Glossy sweep sheen */}
        {enableGlossySweep && (
          <div
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              width: "60%",
              background:
                "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.12), transparent)",
              transform: `translateX(${sweepProgress}%) skewX(-20deg)`,
              pointerEvents: "none",
            }}
          />
        )}
      </div>
    </div>
  );
};
